// The layer of the pirate dens (v2-the-deep.md §8.5, R2720,
// ui-map-interface.md §6.1.13 "The dens"): the slot `dens`, right after
// `sites` and under the sea objects.
//
// It draws the ten designs of round 118 (`render/den-marks.ts`) at the
// cells of `Empire.dens`, in the state of each row. Without the flag
// `pirates`, or with no row, it draws nothing, so the map is the map of
// before. A den reads its state from the den alone: the fleet layer draws
// the hulls of its group that the viewer hears.
//
// Each den owns two paint sheets (`batches.ts` `SheetSink`):
//
// - the BODY sheet holds what stands still. It paints when the picture of
//   the den changes: the design, the state, the cache, the respawn figure
//   (one time in a world minute, one time in a world second under an
//   hour, R2741), the motion setting or the scale step. A
//   pan or a frame paints nothing.
// - the MOTION sheet holds the moving part of the design (the lantern, the
//   lead, a loose line, a chain, the chevrons, the flag) and the top that
//   stands over it (the track, the figure, the cache mark). It paints each
//   frame, and only while the den is in the view, the design moves in its
//   state and the viewer did not ask for less motion. Its box is the small
//   box of the moving part.
//
// A sheet is baked at the device scale of the zoom step, two at the least
// and four at the most. A den off the view paints nothing and draws
// nothing.
//
// The chart fragment (R2700) names the cell of a den and the point of its
// pirate cache (R2859). The den row of a fragment holder carries the cache
// mark, so the den layer draws it at the point of the design, or at the
// fragment point where the hub brought the cache back into the den cell.
// A fragment whose den has no row draws the cache mark alone at its point;
// a fragment whose den has a row draws no second mark.

import type { DrawCtx, FrameInfo, Layer } from '../layer';
import { type BatchSource, type SheetSink, sheetOf } from './batches';
import { sheetScale } from './sheetpaint';
import type { ChartFragment, DenInfo } from '../../gen/proto/aquaphobia';
import { CHART_MONO_FAMILY } from '../gfx/atlas';
import { cellCenterX, cellCenterY, MPX_PER_PX } from '../../world';
import { reducedMotion } from '../../render/motion';
import { leftKey, leftSpan } from '../../locale/span';
import {
  CACHE_MARK_BOX, denBoxes, denMoves, paintCacheMark, paintDenTop, unionBox,
  type DenBox, type DenForm, type DenTop,
} from '../../render/den-marks';
import { denCacheMark, denDesign, denForm } from '../../dens';

/** Where the layer reads the world. */
export interface DenFeeds {
  /** The rows of `Empire.dens`; none while `pirates` is off. */
  dens(): readonly DenInfo[];
  /** The chart fragments of the reader; none while `pirates` is off. */
  fragments(): readonly ChartFragment[];
  /** The world seconds until the group of a sunk den sails again. */
  leftSec(d: DenInfo): number;
}

/** The scale of a den sheet: the zoom step, two at the least, four at the most. */
export function denScale(pxPerWorld: number): number {
  return Math.min(4, Math.max(2, sheetScale(pxPerWorld)));
}

/** The two sheets of one den and what they last painted. */
interface Slot {
  body: SheetSink;
  motion: SheetSink;
  /** The picture the body sheet holds; NaN for none. */
  key: number;
  /** The motion sheet draws this frame. */
  moving: boolean;
  /** The respawn figure and the step it was written for: the world
   *  second under an hour, the world minute from one hour on (R2741). */
  figStep: number;
  fig: string;
}

/** The forms by number, for the picture key. */
const FORM_NO: Readonly<Record<DenForm, number>> = { home: 0, sea: 1, sunk: 2 };
const CACHE_NO = { none: 0, seen: 1, taken: 2 } as const;

/** The key of a lone fragment mark: no design shares it. */
const LONE_KEY_BASE = -1;

export class DenLayer implements Layer {
  readonly name = 'dens';
  private shown = true;
  private motion = !reducedMotion();
  /** The sheets of each den by den id; a lone fragment mark by −den. */
  private readonly slots = new Map<number, Slot>();
  private readonly drawn: Slot[] = [];
  private readonly top: DenTop = { form: 'home', cache: 'none', fig: '', face: CHART_MONO_FAMILY, cacheAt: null };
  /** How many dens and lone marks drew on the last frame. The tests read it. */
  count = 0;
  /** How many times a body sheet painted. The tests read it. */
  bodyPaints = 0;

  constructor(private readonly src: BatchSource, private readonly feeds: DenFeeds) {}

  /** Less motion: every moving part stands at the still pose of the page. */
  setMotion(on: boolean): void { this.motion = on; }

  update(frame: FrameInfo): void {
    const drawn = this.drawn;
    drawn.length = 0;
    const scale = denScale(frame.cam.pxPerWorld), t = frame.timeS, cull = frame.cull;
    const rows = this.feeds.dens();
    const top = this.top;
    for (const d of rows) {
      const des = denDesign(d), form = denForm(d.state);
      if (!des || !form) continue;
      const x = cellCenterX(d.cell), y = cellCenterY(d.cell);
      const cache = denCacheMark(d.cache);
      const boxes = denBoxes(des, form, cache);
      if (!inView(x, y, boxes.full, cull)) continue;
      const moves = this.motion && denMoves(des, form);
      const step = form === 'sunk' ? leftKey(this.feeds.leftSec(d)) : 0;
      const slot = this.slot(d.den);
      // Everything the body sheet shows, as one number: the design, the
      // form, the cache, the motion, the scale step and the figure step. While
      // the den moves, the figure rides the motion sheet.
      const at = cache === 'none' ? null : this.cacheAt(d, des.cacheAt, x, y);
      const key = des.card + 10 * FORM_NO[form] + 30 * CACHE_NO[cache] + (moves ? 90 : 0)
        + 180 * Math.round(scale * 4) + 4000 * (moves ? 0 : step) + (at ? 0.5 : 0);
      top.form = form; top.cache = cache; top.fig = form === 'sunk' ? this.figure(slot, step) : '';
      top.cacheAt = at;
      if (slot.key !== key) {
        slot.key = key;
        this.bodyPaints++;
        const c = this.begin(slot.body, x, y, moves ? des.box : withMark(boxes.full, at), scale);
        if (c) {
          des.body(c, form);
          if (!moves) { des.moving?.(c, form, 0, false); paintDenTop(c, des, top); }
          this.end(slot.body, c);
        }
      }
      slot.body.shown = true;
      slot.moving = moves;
      slot.motion.shown = moves;
      if (moves && boxes.motion) {
        const c = this.begin(slot.motion, x, y, withMark(boxes.motion, at), scale);
        if (c) {
          des.moving!(c, form, t, true);
          paintDenTop(c, des, top);
          this.end(slot.motion, c);
        }
      }
      drawn.push(slot);
    }
    this.lone(rows, scale, cull);
    this.count = drawn.length;
  }

  /** The point of the cache mark of a den round its origin when the
   *  fragment names another point than the design, else null (R2859). */
  private cacheAt(d: DenInfo, design: readonly [number, number], x: number, y: number): readonly [number, number] | null {
    for (const f of this.feeds.fragments()) {
      if (f.den !== d.den || (f.xMpx === 0 && f.yMpx === 0)) continue;
      const dx = f.xMpx / MPX_PER_PX - x, dy = f.yMpx / MPX_PER_PX - y;
      return Math.abs(dx - design[0]) < 0.5 && Math.abs(dy - design[1]) < 0.5 ? null : [dx, dy];
    }
    return null;
  }

  /** The cache mark of each fragment whose den has no row, at the point of
   *  its cache (R2859); a fragment of an older server takes its cell. */
  private lone(rows: readonly DenInfo[], scale: number, cull: FrameInfo['cull']): void {
    for (const f of this.feeds.fragments()) {
      let known = false;
      for (const r of rows) if (r.den === f.den && denDesign(r) && denForm(r.state)) { known = true; break; }
      if (known) continue;
      const point = f.xMpx !== 0 || f.yMpx !== 0;
      const x = point ? f.xMpx / MPX_PER_PX : cellCenterX(f.cell);
      const y = point ? f.yMpx / MPX_PER_PX : cellCenterY(f.cell);
      if (!inView(x, y, CACHE_MARK_BOX, cull)) continue;
      const slot = this.slot(LONE_KEY_BASE - f.den);
      const key = (f.taken ? 2 : 1) + 180 * scale;
      if (slot.key !== key) {
        slot.key = key;
        this.bodyPaints++;
        const c = this.begin(slot.body, x, y, CACHE_MARK_BOX, scale);
        if (c) { paintCacheMark(c, 0, 0, f.taken); this.end(slot.body, c); }
      }
      slot.body.shown = true;
      slot.moving = false;
      slot.motion.shown = false;
      this.drawn.push(slot);
    }
  }

  private slot(id: number): Slot {
    let s = this.slots.get(id);
    if (!s) {
      s = { body: sheetOf(this.src), motion: sheetOf(this.src), key: NaN, moving: false, figStep: -1, fig: '' };
      this.slots.set(id, s);
    }
    return s;
  }

  /** The respawn figure of a slot, written again only when its step moves.
   *  A sunk den with no time left writes none, as the den card does: a
   *  quiet den with no free cell reads as sunk with 0 s (R2901). */
  private figure(slot: Slot, step: number): string {
    if (slot.figStep !== step) { slot.figStep = step; slot.fig = step > 0 ? leftSpan(step) : ''; }
    return slot.fig;
  }

  /** Begins a paint of a box round the den centre, with the origin on the
   *  centre; null where the sheet has no canvas. */
  private begin(sheet: SheetSink, x: number, y: number, b: DenBox, scale: number): CanvasRenderingContext2D | null {
    const c = sheet.begin(x + b[0], y + b[1], b[2] - b[0], b[3] - b[1], scale);
    if (c) { c.save(); c.translate(x, y); }
    return c;
  }

  private end(sheet: SheetSink, c: CanvasRenderingContext2D): void {
    c.restore();
    sheet.end();
  }

  draw(ctx: DrawCtx): void {
    if (!this.shown) return;
    for (const s of this.drawn) {
      s.body.flush(ctx.cam);
      if (s.moving) s.motion.flush(ctx.cam);
    }
  }

  setVisible(v: boolean): void { this.shown = v; }
  get visible(): boolean { return this.shown; }
}

/** A sheet box that also holds a cache mark at a fragment point, or the
 *  box itself when the mark stands at the point of the design. */
function withMark(b: DenBox, at: readonly [number, number] | null): DenBox {
  if (!at) return b;
  return unionBox(b, [at[0] + CACHE_MARK_BOX[0], at[1] + CACHE_MARK_BOX[1], at[0] + CACHE_MARK_BOX[2], at[1] + CACHE_MARK_BOX[3]])!;
}

/** True when a box round a centre meets the view. */
function inView(x: number, y: number, b: DenBox, v: FrameInfo['cull']): boolean {
  return x + b[2] >= v.x0 && x + b[0] <= v.x1 && y + b[3] >= v.y0 && y + b[1] <= v.y1;
}
